import { Controller } from "@hotwired/stimulus"

// Invert dark seulement si :
// 1) l’image a du vide (transparence significative)
// 2) le contenu opaque est suffisamment sombre
// 3) ce contenu n’est pas une plaque quasi pleine (bbox remplie)
// Sinon : padding fond UI (.brand-logo-solid), logo intact.
// Bordure sidebar (.brand-logo-bordered) seulement si le fichier n’a pas de transparence.
// Logo détouré (.brand-logo-cutout : du vide, et pas une plaque), sombre ou clair : il n’a
// pas de fond à arrondir, et son contenu touche souvent les bords du fichier.
export default class extends Controller {
  static values = {
    voidRatio: { type: Number, default: 0.12 },
    darkLuminance: { type: Number, default: 0.40 },
    maxOpaqueFill: { type: Number, default: 0.78 }
  }

  connect() {
    const img = this.element
    if (!(img instanceof HTMLImageElement)) return

    this.analyzeBound = () => this.analyze()
    if (img.complete && img.naturalWidth > 0) {
      this.analyze()
    } else {
      img.addEventListener("load", this.analyzeBound, { once: true })
    }
  }

  disconnect() {
    this.element.removeEventListener("load", this.analyzeBound)
  }

  analyze() {
    const img = this.element
    if (!(img instanceof HTMLImageElement) || img.naturalWidth === 0) {
      this.applySolid()
      return
    }

    try {
      const { hasVoid, isDark, isPlate } = this.sample(img)
      if (hasVoid && isDark && !isPlate) {
        this.applyInvert()
      } else {
        this.applySolid({ opaqueBackground: !hasVoid })
      }
      this.element.classList.toggle("brand-logo-cutout", hasVoid && !isPlate)
    } catch (_error) {
      // Canvas tainted / format non décodable → safe default
      this.applySolid()
    }
  }

  sample(img) {
    const maxW = 240
    const maxH = 120
    const scale = Math.min(1, maxW / img.naturalWidth, maxH / img.naturalHeight)
    const width = Math.max(1, Math.round(img.naturalWidth * scale))
    const height = Math.max(1, Math.round(img.naturalHeight * scale))

    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    ctx.drawImage(img, 0, 0, width, height)

    const { data } = ctx.getImageData(0, 0, width, height)
    const total = data.length / 4
    let transparent = 0
    let opaque = 0
    let luminanceSum = 0
    let minX = width
    let minY = height
    let maxX = -1
    let maxY = -1

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3]
      if (alpha < 24) {
        transparent += 1
        continue
      }

      const pixelIndex = i / 4
      const x = pixelIndex % width
      const y = Math.floor(pixelIndex / width)
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y

      // Ignore anti-alias semi-transparent for luminance
      if (alpha < 200) continue

      opaque += 1
      luminanceSum += (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255
    }

    const hasVoid = total > 0 && transparent / total >= this.voidRatioValue
    const avgLuminance = opaque > 0 ? luminanceSum / opaque : 1
    const isDark = avgLuminance <= this.darkLuminanceValue

    let isPlate = true
    if (opaque > 0 && maxX >= minX && maxY >= minY) {
      const bboxArea = (maxX - minX + 1) * (maxY - minY + 1)
      const fillRatio = opaque / bboxArea
      isPlate = fillRatio >= this.maxOpaqueFillValue
    }

    return { hasVoid, isDark, isPlate }
  }

  applyInvert() {
    this.element.classList.remove("brand-logo-solid", "brand-logo-bordered")
    this.element.classList.add("brand-logo")
  }

  applySolid({ opaqueBackground = false } = {}) {
    this.element.classList.remove("brand-logo", "brand-logo-cutout")
    this.element.classList.add("brand-logo-solid")
    this.element.classList.toggle("brand-logo-bordered", opaqueBackground)
  }
}
